/**
 * PHOTOS §4, keeping: the pictures on this device. NOT localStorage - that carries the profile and is mirrored whole
 * on every change - but IndexedDB (`idb.ts`), behind `PhotoBackend` so the tests run on a Map. Nothing here throws:
 * a store that fails is a store with nothing in it, and no picture is ever worth an error.
 */

import { MAX_PHOTOS_KEPT } from './types';

/** One picture as it is kept. */
export interface PhotoRecord {
  id: string;
  jpeg: Blob;
  /** When it was taken (or, for one fetched from the server, kept here): what "newest" means. */
  at: number;
  /** The save server holds it under this save code, so it follows the save to any device. */
  uploaded: boolean;
}

/** The few calls the store needs from IndexedDB. May reject; the store turns that into "nothing". */
export interface PhotoBackend {
  get(id: string): Promise<PhotoRecord | null>;
  put(record: PhotoRecord): Promise<void>;
  delete(id: string): Promise<void>;
  all(): Promise<PhotoRecord[]>;
}

/** A backend over a Map: the tests' IndexedDB. */
export function memoryBackend(records = new Map<string, PhotoRecord>()): PhotoBackend & {
  records: Map<string, PhotoRecord>;
} {
  return {
    records,
    get: async (id) => records.get(id) ?? null,
    put: async (record) => void records.set(record.id, { ...record }),
    delete: async (id) => void records.delete(id),
    all: async () => [...records.values()].map((r) => ({ ...r })),
  };
}

export interface PhotoStore {
  /**
   * False once it is known there is nowhere to keep a picture (no IndexedDB: a private window, node). The camera
   * then takes none. True while the database is still opening: it almost always opens.
   */
  usable(): boolean;
  get(id: string): Promise<PhotoRecord | null>;
  /** Keep one, then drop the oldest past `max`. False when it could not be kept. */
  keep(record: PhotoRecord): Promise<boolean>;
  /** Those the server does not hold yet, oldest first. */
  pending(): Promise<PhotoRecord[]>;
  markUploaded(id: string): Promise<void>;
}

const newestFirst = (a: PhotoRecord, b: PhotoRecord): number => b.at - a.at || (a.id < b.id ? -1 : 1);

/** The store over a backend that may still be opening, or that turned out not to exist (null). */
export function createPhotoStore(
  source: PhotoBackend | null | Promise<PhotoBackend | null>,
  max = MAX_PHOTOS_KEPT,
): PhotoStore {
  let state: 'opening' | 'open' | 'off' = 'opening';
  const opened: Promise<PhotoBackend | null> = Promise.resolve(source)
    .catch(() => null)
    .then((backend) => {
      state = backend === null ? 'off' : 'open';
      return backend;
    });

  /** One backend call, or `fallback` when there is no backend or the call failed. */
  async function use<T>(fallback: T, work: (backend: PhotoBackend) => Promise<T>): Promise<T> {
    try {
      const backend = await opened;
      return backend === null ? fallback : await work(backend);
    } catch {
      return fallback;
    }
  }

  return {
    usable: () => state !== 'off',

    get: (id) => use(null, (b) => b.get(id)),

    keep: (record) => use(false, async (b) => {
      await b.put(record);
      const all = (await b.all()).sort(newestFirst);
      for (const old of all.slice(max)) await b.delete(old.id);
      return all.slice(0, max).some((r) => r.id === record.id);
    }),

    pending: () => use([], async (b) => (await b.all()).filter((r) => !r.uploaded).sort((a, b2) => newestFirst(b2, a))),

    markUploaded: (id) => use(undefined, async (b) => {
      const record = await b.get(id);
      if (record !== null && !record.uploaded) await b.put({ ...record, uploaded: true });
    }),
  };
}
